<script setup lang="ts">

import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  Title,
  Tooltip
} from "chart.js";
import { Pie} from "vue-chartjs";
import {computed} from "vue";
import {useI18n} from "vue-i18n";

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement);

interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string[];
  }[];
}

type ChartKeys = {
  [key: string]: string[];
};

const {t} = useI18n();

const props = defineProps(
  {
    curriculum: {
      type: String,
      required: true,
    },
    subjectGroup: {
      type: String,
      required: true,
    },
    reportData: {
      type: Object,
      required: true,
    },
  }
);

const chartData = computed(() => {
  if (!chartKeyArray[props.curriculum])
    return null;

  const sortedReportData = Object.entries(props.reportData)
    .sort(([, a], [, b]) => b - a)
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {} as Record<string, number>);

  return createPieChartData(
    sortedReportData,
    t(`curriculums.${props.curriculum}.subject_groups.${props.subjectGroup}`)
  );
});

/**
 * Creates the pie chart data for a given subject count
 * @param subjectCounts The subject counts
 * @param label The label for the chart
 */
function createPieChartData(subjectCounts: unknown, label: string): ChartData {
  if (subjectCounts)
    return {
      labels: Object.keys(subjectCounts)
        .map((subjectLabel) => t(`curriculums.${props.curriculum}.subjects.${subjectLabel}`)),
      datasets: [
        {
          label,
          data: Object.values(subjectCounts),
          backgroundColor: colors,
        },
      ],
    };

  return {
    labels: [],
    datasets: [
      {
        label,
        data: [],
        backgroundColor: colors,
      },
    ],
  };
}

const colors = [
  "#4e79a7",
  "#f28e2b",
  "#e15759",
  "#76b7b2",
  "#59a14f",
  "#edc948",
  "#af7aa1",
  "#ff9da7",
  "#9c755f",
  "#bab0ac",
];

const chartKeyArray: ChartKeys = {
  "national_senior_certificate": ["first_language", "second_language", "mathematics"],
  "cambridge": ["group_1", "group_2", "group_3", "group_4", "group_5", "ungrouped"],
  "international_baccalaureate": ["group_1", "group_2", "group_3", "group_4", "group_5", "group_6"],
  "pre_international_baccalaureate": ["group_1", "group_2", "group_3", "group_4", "group_5", "group_6"],
  "kenyan_certificate_of_secondary_education": ["science_1", "science_2", "humanities"],
};

const chartOptions: ChartOptions<"pie"> = {
  responsive: true,
  plugins: {
    legend: {
      position: "bottom"
    }
  }
};

</script>
<template>
  <Pie v-if="chartData" :options="chartOptions" :data="chartData"/>
</template>
